import React, { useState, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
    Alert,
    Box,
    Button,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    IconButton,
    Divider,
    Collapse,
    Tooltip,
    useTheme,
    useMediaQuery,
    alpha,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    HealthAndSafety as HealthIcon,
    History as AuditIcon,
    Settings as SettingsIcon,
    BarChart as BarChartIcon,
    Campaign as CampaignIcon,
    ListAlt as ListAltIcon,
    WaterDrop as HydrantIcon,
    Router as RpiIcon,
    Assignment as AssignmentIcon,
    LocalShipping as LocalShippingIcon,
    FactCheck as FactCheckIcon,
    ExpandLess,
    ExpandMore,
    EventAvailable as EventAvailableIcon,
    WarningAmber as WarningAmberIcon,
    Description as DescriptionIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Inventory2 as InventoryIcon,
    Checklist as ChecklistIcon,
    BackpackOutlined as BackpackIcon,
    Sensors as SensorsIcon,
    Home as HomeIcon,
    Category as CategoryIcon,
    CalendarMonth as CalendarMonthIcon,
    ContactMail as ContactMailIcon,
    Map as MapIcon,
    List as ListIcon,
    Event as EventIcon,
    Warning as WarningIcon,
    Chat as ChatIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useJednotkaQuery } from '../api/JsdhNotifikaceService/service/AdminMutations';
import { useDemoStatusQuery } from '../api/JsdhNotifikaceService/service/FeedbackMutations';
import { useKanalJednotky } from '../api/JsdhNotifikaceService/service/ChatMutations';
import AdminBreadcrumbs from './AdminBreadcrumbs';
import AppearanceMenu from './AppearanceMenu';
import PushNotificationToggle from './PushNotificationToggle';
import { UserDropdownMenu, MobileBottomNav } from './admin';
import IotDetekceVypnutaBanner from './iot/IotDetekceVypnutaBanner';
import { filterNavItems } from './admin/navVisibility';
import type { NavItem, NavGroup } from './admin/navVisibility';
import { useFeatureFlags } from '../hooks/useFeatureFlag';
import { useThemeMode } from '../theme/ThemeContext';
import { getColorScheme } from '../theme/colorSchemes';
import { createAdminTheme } from '../theme/createAdminTheme';

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 80;

const navGroups: NavGroup[] = [
    {
        items: [
            { label: 'Dashboard', path: '/admin', icon: <DashboardIcon /> },
            {
                label: 'Poplachy',
                path: '',
                icon: <CampaignIcon />,
                children: [
                    { label: 'Šablony poplachu', path: '/admin/sablony', icon: <DescriptionIcon />, adminOnly: true },
                    { label: 'Seznam událostí', path: '/admin/udalosti', icon: <ListAltIcon /> },
                ],
            },
            { label: 'Moje výstroj / OOP', path: '/admin/moje-vybaveni', icon: <BackpackIcon />, featureFlag: 'MODUL_INVENTAR' },
        ],
    },
    {
        label: 'Jednotka',
        items: [
            { label: 'Nástěnka', path: '/admin/nastenka', icon: <CampaignIcon />, featureFlag: 'MODUL_NASTENKA' },
            { label: 'Uživatelé', path: '/admin/users', icon: <PeopleIcon />, adminOnly: true },
            { label: 'Externí kontakty', path: '/admin/externi-kontakty', icon: <ContactMailIcon />, adminOnly: true, featureFlag: 'MODUL_EXTERNI_KONTAKTY' },
            { label: 'Specializace', path: '/admin/specializace', icon: <AssignmentIcon />, adminOnly: true },
            { label: 'Technika', path: '/admin/technika', icon: <LocalShippingIcon /> },
            { label: 'Inventář', path: '/admin/inventar', icon: <InventoryIcon />, adminOnly: true, featureFlag: 'MODUL_INVENTAR' },
            { label: 'Inventura', path: '/admin/inventura', icon: <ChecklistIcon />, adminOnly: true, featureFlag: 'MODUL_INVENTAR' },
            { label: 'Kontroly', path: '/admin/kontroly', icon: <FactCheckIcon />, adminOnly: true },
            { label: 'Dostupnost', path: '/admin/dostupnost', icon: <EventAvailableIcon />, adminOnly: true },
            { label: 'Pohotovost', path: '/admin/pohotovost', icon: <WarningAmberIcon />, adminOnly: true },
            { label: 'Akce', path: '/admin/akce', icon: <CalendarMonthIcon />, adminOnly: true, featureFlag: 'MODUL_AKCE' },
            { label: 'Hydranty', path: '/admin/hydranty', icon: <HydrantIcon />, adminOnly: true },
        ],
    },
    {
        label: 'Obec',
        items: [
            {
                label: 'Hydranty',
                path: '',
                icon: <HydrantIcon />,
                adminOnly: true,
                children: [
                    { label: 'Přehled', path: '/admin/hydranty-evidence', icon: <DashboardIcon />, adminOnly: true },
                    { label: 'Mapa', path: '/admin/hydranty-evidence/mapa', icon: <MapIcon />, adminOnly: true },
                    { label: 'Seznam', path: '/admin/hydranty-evidence/seznam', icon: <ListIcon />, adminOnly: true },
                    { label: 'Plány', path: '/admin/hydranty-evidence/plany', icon: <EventIcon />, adminOnly: true },
                    { label: 'Závady', path: '/admin/hydranty-evidence/zavady', icon: <WarningIcon />, adminOnly: true },
                ],
            },
        ],
    },
    {
        label: 'Přehledy',
        items: [
            { label: 'Statistiky & Reporty', path: '/admin/statistiky', icon: <BarChartIcon /> },
        ],
    },
    {
        label: 'Systém',
        items: [
            { label: 'Zdraví systému', path: '/admin/health', icon: <HealthIcon /> },
            { label: 'Audit log', path: '/admin/audit', icon: <AuditIcon />, adminOnly: true },
            {
                label: 'IoT Monitoring',
                path: '',
                icon: <SensorsIcon />,
                adminOnly: true,
                featureFlag: 'MODUL_IOT',
                children: [
                    { label: 'Dashboard', path: '/admin/iot', icon: <DashboardIcon />, adminOnly: true },
                    { label: 'Objekty', path: '/admin/iot/objekty', icon: <HomeIcon />, adminOnly: true },
                    { label: 'Evidence zařízení', path: '/admin/iot/zarizeni', icon: <InventoryIcon />, adminOnly: true },
                    { label: 'Typy senzorů', path: '/admin/iot/senzor-typy', icon: <CategoryIcon />, adminOnly: true },
                    { label: 'Nastavení', path: '/admin/iot/nastaveni', icon: <SettingsIcon />, adminOnly: true },
                ],
            },
            {
                label: 'Konfigurace',
                path: '',
                icon: <SettingsIcon />,
                adminOnly: true,
                children: [
                    { label: 'Systém', path: '/admin/configuration', icon: <SettingsIcon />, adminOnly: true },
                    { label: 'Zařízení', path: '/admin/devices', icon: <RpiIcon />, adminOnly: true },
                ],
            },
        ],
    },
];

const AdminLayout: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useAuth();
    const { isFeatureEnabled, isLoading: flagyNacitaji } = useFeatureFlags();
    const { data: jednotka } = useJednotkaQuery();
    const { data: demoStatus } = useDemoStatusQuery();
    const demoEnabled = demoStatus?.demoEnabled === true;
    const { colorScheme, isDark } = useThemeMode();

    // HO-147 Task 4: "Chat jednotky" v menu i bez MODUL_CHAT, pokud uz existuje
    // historie (posledniZprava) - flag gatuje jen PSANI, cteni ma zustat
    // dosazitelne (stejne pravidlo jako v mobilu).
    //
    // Dotaz se pousti JEN kdyz je flag vypnuty, a BEZ pollovani. Duvod: ten GET
    // je na backendu insert-or-get (`ziskejNeboZalozKanalJednotky`), takze
    // nepodminene volani z layoutu zakladalo konverzaci pri otevreni KAZDE admin
    // stranky - i u jednotek, ktere chat nikdy nezaply - a poll k tomu pridaval
    // ctyri pozadavky za minutu z kazde otevrene zalozky. Se zapnutym flagem je
    // polozka viditelna tak jako tak, takze odpoved menu nepotrebuje.
    // Na CHLADNEM STARTU je `isFeatureEnabled` fail-closed a vraci false driv, nez
    // dorazi app-config - dotaz by tedy vystartoval i u jednotky, ktera chat ZAPNUTY
    // MA, jen se to jeste nevi. Ceka se proto, az jsou flagy znama; do te doby se
    // polozka menu stejne nema podle ceho zobrazit.
    //
    // ZNAMY ZBYTEK: i tak plati, ze u jednotky s VYPNUTYM flagem tenhle GET konverzaci
    // zalozi - endpoint je na backendu insert-or-get a cist historii bez zalozeni dnes
    // nejde. Dopad je jeden prazdny radek v `konverzace`, zadne zpravy a polozka menu
    // zustane skryta (`posledniZprava` je null); tyz radek by stejne vznikl v okamziku,
    // kdy nekdo chat zapne. Ciste by to spravila az ctecí varianta endpointu
    // (napr. `?zalozit=false`) - na jeden prazdny radek je to nepomer.
    const chatFlagZapnuty = isFeatureEnabled('MODUL_CHAT');
    const { data: kanalJednotky } = useKanalJednotky(!flagyNacitaji && !chatFlagZapnuty, false);
    const chatViditelny = chatFlagZapnuty || !!kanalJednotky?.posledniZprava;
    const computedNavGroups: NavGroup[] = navGroups.map((group) =>
        group.label === 'Jednotka' && chatViditelny
            ? {
                  ...group,
                  items: [
                      ...group.items,
                      { label: 'Chat jednotky', path: '/admin/chat', icon: <ChatIcon /> },
                  ],
              }
            : group
    );

    // Sidebar collapsed state persisted in localStorage
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem('admin-sidebar-collapsed') === 'true';
        } catch {
            return false;
        }
    });

    const toggleSidebarCollapsed = () => {
        const next = !sidebarCollapsed;
        setSidebarCollapsed(next);
        try {
            localStorage.setItem('admin-sidebar-collapsed', String(next));
        } catch { /* ignore */ }
    };

    // Create admin-scoped theme
    const adminTheme = useMemo(() => {
        const scheme = getColorScheme(colorScheme);
        return createAdminTheme(scheme, isDark);
    }, [colorScheme, isDark]);

    const drawerWidth = isMobile
        ? DRAWER_WIDTH_EXPANDED
        : sidebarCollapsed
        ? DRAWER_WIDTH_COLLAPSED
        : DRAWER_WIDTH_EXPANDED;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleNavigation = (path: string) => {
        navigate(path);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const handleExpandToggle = (path: string) => {
        setExpandedItems((prev) =>
            prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
        );
    };

    const isChildActive = (item: NavItem): boolean => {
        return item.children?.some((child) => location.pathname === child.path) || false;
    };

    const isCollapsed = !isMobile && sidebarCollapsed;

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Toolbar
                sx={{
                    justifyContent: isCollapsed ? 'center' : 'space-between',
                    px: isCollapsed ? 0 : 2,
                    minHeight: '64px !important',
                }}
            >
                {!isCollapsed && (
                    <Typography variant="subtitle2" noWrap fontWeight={600} color="text.secondary" sx={{ letterSpacing: '0.02em' }}>
                        Menu
                    </Typography>
                )}
                {!isMobile && (
                    <IconButton onClick={toggleSidebarCollapsed} size="small" sx={{ color: 'text.secondary' }}>
                        {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                    </IconButton>
                )}
            </Toolbar>
            <Divider />
            <List sx={{ flexGrow: 1, px: isCollapsed ? 0.5 : 0 }}>
                {computedNavGroups.map((group) => {
                    const visibleItems = filterNavItems(group.items, isAdmin, isFeatureEnabled);
                    if (visibleItems.length === 0) return null;
                    return (
                        <React.Fragment key={group.label ?? '_top'}>
                            {group.label && !isCollapsed && (
                                <ListSubheader
                                    sx={{
                                        fontSize: 11,
                                        lineHeight: '32px',
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: 'text.secondary',
                                        bgcolor: 'transparent',
                                        mt: 1,
                                        fontWeight: 700,
                                    }}
                                >
                                    {group.label}
                                </ListSubheader>
                            )}
                            {isCollapsed && group.label && <Divider sx={{ my: 0.5, mx: 1 }} />}
                            {visibleItems.map((item) => (
                                <React.Fragment key={item.label}>
                                    <ListItem disablePadding>
                                        <Tooltip
                                            title={isCollapsed ? item.label : ''}
                                            placement="right"
                                            arrow
                                        >
                                            <ListItemButton
                                                selected={item.path ? location.pathname === item.path : isChildActive(item)}
                                                onClick={() => {
                                                    if (item.children) {
                                                        handleExpandToggle(item.label);
                                                    }
                                                    if (item.path) {
                                                        handleNavigation(item.path);
                                                    }
                                                }}
                                                sx={{
                                                    minHeight: 44,
                                                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                                                    px: isCollapsed ? 1.5 : 2.5,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: isCollapsed ? 0 : 40,
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {item.icon}
                                                </ListItemIcon>
                                                {!isCollapsed && (
                                                    <>
                                                        <ListItemText
                                                            primary={item.label}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.875rem',
                                                                fontWeight: location.pathname === item.path ? 600 : 400,
                                                            }}
                                                        />
                                                        {item.children && (
                                                            expandedItems.includes(item.label) ? (
                                                                <ExpandLess sx={{ fontSize: 18 }} />
                                                            ) : (
                                                                <ExpandMore sx={{ fontSize: 18 }} />
                                                            )
                                                        )}
                                                    </>
                                                )}
                                            </ListItemButton>
                                        </Tooltip>
                                    </ListItem>
                                    {item.children && !isCollapsed && (
                                        <Collapse
                                            in={expandedItems.includes(item.label)}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <List
                                                component="div"
                                                disablePadding
                                                sx={{ ml: 4.5, position: 'relative', pt: 0 }}
                                            >
                                                {item.children.map((child, idx, arr) => (
                                                        <ListItem
                                                            key={child.path}
                                                            disablePadding
                                                            sx={{
                                                                position: 'relative',
                                                                '&::before': {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    left: 0,
                                                                    top: 0,
                                                                    bottom: idx === arr.length - 1 ? '50%' : 0,
                                                                    width: '1px',
                                                                    bgcolor: 'divider',
                                                                },
                                                                '&::after': {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    left: 0,
                                                                    top: '50%',
                                                                    width: 12,
                                                                    height: '1px',
                                                                    bgcolor: 'divider',
                                                                },
                                                            }}
                                                        >
                                                            <ListItemButton
                                                                selected={location.pathname === child.path}
                                                                onClick={() => handleNavigation(child.path)}
                                                                sx={{ pl: 2.5, py: 0.5, minHeight: 36 }}
                                                            >
                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                    {child.icon}
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={child.label}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.8125rem',
                                                                        fontWeight: location.pathname === child.path ? 600 : 400,
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        </Collapse>
                                    )}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    );
                })}
            </List>
        </Box>
    );

    return (
        <ThemeProvider theme={adminTheme}>
            <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
                <AppBar
                    position="fixed"
                    sx={{
                        width: { md: `calc(100% - ${drawerWidth}px)` },
                        ml: { md: `${drawerWidth}px` },
                        transition: 'width 0.2s ease, margin-left 0.2s ease',
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { md: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ flexGrow: 1, fontWeight: 600, fontSize: '1.1rem' }}
                        >
                            {jednotka?.nazev || 'Hasiči Online'} - Administrace
                        </Typography>
                        {isAdmin && (
                            <>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    startIcon={<CampaignIcon />}
                                    onClick={() => navigate('/admin/poplach')}
                                    sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }}
                                >
                                    Vyhlásit poplach
                                </Button>
                                <Tooltip title="Vyhlásit poplach">
                                    <IconButton
                                        color="error"
                                        onClick={() => navigate('/admin/poplach')}
                                        sx={{ display: { xs: 'flex', sm: 'none' }, mr: 0.5 }}
                                    >
                                        <CampaignIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        <PushNotificationToggle />
                        <AppearanceMenu />
                        <UserDropdownMenu />
                    </Toolbar>
                </AppBar>

                <Box
                    component="nav"
                    sx={{
                        width: { md: drawerWidth },
                        flexShrink: { md: 0 },
                        transition: 'width 0.2s ease',
                    }}
                >
                    {/* Mobile drawer */}
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: DRAWER_WIDTH_EXPANDED,
                            },
                        }}
                    >
                        {drawer}
                    </Drawer>
                    {/* Desktop drawer */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: drawerWidth,
                                transition: 'width 0.2s ease',
                                overflowX: 'hidden',
                            },
                        }}
                        open
                    >
                        {drawer}
                    </Drawer>
                </Box>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        px: { xs: 1.5, sm: 2, md: 3 },
                        py: { xs: 2, sm: 3 },
                        width: { md: `calc(100% - ${drawerWidth}px)` },
                        mt: '64px',
                        pb: { xs: '88px', md: 3 },
                        overflow: 'hidden',
                        transition: 'width 0.2s ease',
                    }}
                >
                    {demoEnabled && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>Demo režim</strong> — Toto je demo verze pro testování. Notifikace nejsou odesílány jako kritické (neobcházejí tichý režim).
                            Vyhlášení poplachů je omezeno na 3/den per zařízení. Veškeré aktivity jsou zaznamenávány.
                        </Alert>
                    )}
                    <AdminBreadcrumbs />
                    <IotDetekceVypnutaBanner />
                    <Outlet />
                </Box>

                {/* Mobile bottom navigation */}
                {isMobile && (
                    <MobileBottomNav navGroups={computedNavGroups} isAdmin={isAdmin} />
                )}
            </Box>
        </ThemeProvider>
    );
};

export default AdminLayout;
